import { useEffect } from 'react';

import useInstoreCart from 'hooks/use-instore-cart';
import useGetInstoreCart from 'src/payments/hooks/use-get-instore-cart/use-get-instore-cart';
import type { InstoreCartQuery } from 'src/payments/hooks/use-get-instore-cart/use-get-instore-cart';
import useFilteredDispensaries from 'src/payments/hooks/use-filtered-dispensaries/use-filtered-dispensaries';
import type { FilteredDispensariesQuery } from 'src/payments/hooks/use-filtered-dispensaries/use-filtered-dispensaries';
import { CartViews } from 'src/payments/state/instore-cart';

type UseInstoreCartDataSyncProps = {
  cartToken: string;
  skip: boolean;
};

type UseInstoreCartDataSyncReturn = {
  cartData: InstoreCartQuery['data'];
  cartLoading: InstoreCartQuery['loading'];
  cartError: InstoreCartQuery['error'];
  dispensaryInfo: FilteredDispensariesQuery['data'];
  // True only while the dispensary query is in flight (skipped until cartData resolves, false
  // again once it settles). Lets callers fail open instead of waiting on a falsy dispensaryInfo,
  // which can't distinguish loading from errored.
  dispensaryInfoLoading: FilteredDispensariesQuery['loading'];
  startPolling: InstoreCartQuery['startPolling'];
  stopPolling: InstoreCartQuery['stopPolling'];
};

// Shared by the `/pay/instore` controller and the one-click PBB flow: fetches the instore-cart +
// dispensary data for `cartToken` and keeps the global `InstoreCart` store's totals/tip/dispensary
// fields in sync with the query result, so `Totals`/`Tip` render correctly regardless of which
// surface populated the token.
export function useInstoreCartDataSync({ cartToken, skip }: UseInstoreCartDataSyncProps): UseInstoreCartDataSyncReturn {
  const InstoreCart = useInstoreCart();
  const { data: cartData, loading: cartLoading, error: cartError, startPolling, stopPolling } = useGetInstoreCart({
    skip,
    cartToken,
  });
  const { data: dispensaryInfo, loading: dispensaryInfoLoading } = useFilteredDispensaries({
    skip: !cartData,
    dispensaryId: cartData?.dispensaryId ?? '',
  });

  useEffect(() => {
    if (!cartLoading && cartData) {
      InstoreCart.setInstoreCart({
        ...cartData,
        dispensary: {
          name: dispensaryInfo?.name,
          logo: dispensaryInfo?.logo,
          id: dispensaryInfo?.dispensaryId,
        },
      });
    } else if (cartError) {
      InstoreCart.setCurrentView(CartViews.ERROR);
    } else {
      InstoreCart.resetDispensary();
    }
  }, [cartLoading, cartData, cartError, InstoreCart, dispensaryInfo]);

  return { cartData, cartLoading, cartError, dispensaryInfo, dispensaryInfoLoading, startPolling, stopPolling };
}
